import { computed, reactive, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { useEventListener } from '@zoomcatalog/composable';
import { useScreenWidth } from '@zoomcatalog/design-system';
import type { Page } from '@zoomcatalog/design-templates';
import { debounce, makeInstanceFactory, throttle } from '@zoomcatalog/shared';
import { ImplicitTokenResponse } from '@/service/request-handlers/useAuthService';
import { GeolocationData } from '@/service/request-handlers/useGeolocationService';
import useTrackingService from '@/service/request-handlers/useTrackingService';
import {
  DesignDownloadTracking,
  PageViewTracking,
  ProductClickTracking,
  ProductImageDownloadTracking,
  ProductLinkClickTracking,
  TimeOnPageTracking,
} from '@/service/tracking-definitions';
import { IProduct } from '@/service/ts-definitions';
import { settings } from '@/settings/DynamicSettings';
import { useProjectStore } from './store-utils/useProjectStore';
import { useCookie } from './useCookie';

export type PagesMap = Record<string, Page>;
type BuildProductClick = {
  product: IProduct | undefined;
  pages: Page[];
};
type BuildPageView = { pages: Page[] };
type BuildTimeOnPage = {
  pages: Page[];
  start_view_timestamp: number;
  end_view_timestamp: number;
};
type BuildProductLinkClick = {
  source: ProductLinkClickTracking['source'];
  component: ProductLinkClickTracking['component'];
  product: IProduct | undefined;
  pages: Page[];
};
type BuildProductImageDownload = {
  product: IProduct | undefined;
  pages: Page[];
  imageUrl: string;
};
type TrackingContext = {
  options?: {
    geolocation?: GeolocationData;
  };
};

export function setImplicitTokenInCookie(implicitToken: ImplicitTokenResponse) {
  const implicitTokenCookie =
    useCookie<ImplicitTokenResponse>('implicit-token');
  const timestamp = new Date().getTime();
  const expireOneDay = timestamp + 60 * 60 * 24 * 1000 * 1;
  const expireDate = new Date(expireOneDay);
  implicitTokenCookie.set(implicitToken, {
    expires: expireDate,
  });
}

const [useMakeTrackingInstance, useTrackingInstance, useTrackingHandlers] =
  makeInstanceFactory('tracking', (context: TrackingContext) => {
    const startViewTimestamp = ref<number | null>(null);
    const previousCurrentPages = ref<Page[]>([]);

    const { projectData } = useProjectStore();
    const trackingService = useTrackingService();

    const screenWidth = useScreenWidth();
    const screenType = computed(() =>
      screenWidth.value === 'sm' ? 'mobile' : 'desktop'
    );

    const sharedEventData = computed(() => ({
      company_id: projectData.value.company_id,
      project_id: projectData.value.project_id,
      project_name: projectData.value.name,
    }));

    const sharedTrackingData = computed(() => ({
      product: 'smart-layouts-viewer' as const,
      product_type: 'application' as const,
      location: location.href,
      is_mobile: screenType.value === 'mobile',
      useragentraw: navigator.userAgent,
      ip_address: context.options?.geolocation?.ip ?? '',
      country: context.options?.geolocation?.country ?? '',
    }));

    // handlers
    function formatPage(page: Page) {
      return {
        id: page.pageId,
        index: page.index,
        layout_id: page.layoutId,
      };
    }

    function buildProductClickEvent(data: BuildProductClick) {
      if (!data.product) {
        Sentry.captureMessage('Product not provided to buildProductClickEvent');
        return;
      }

      const variation = data.product.variations.find(
        (variation) => variation.part_id === data.product?.part_id
      );

      const productClickEvent: ProductClickTracking = {
        source: 'canvas',
        component: 'product-card',
        groupings: 'user-tracking-actions.product-click',

        event_data: {
          ...sharedEventData.value,

          product_id: data.product.product_id,
          product_name: data.product.name,
          product_category: data.product.category,
          product_brand: data.product.brand,
          product_style_id: data.product.style_id,
          product_style_name: data.product.style_name,
          product_price: data.product.price,
          variation_id: variation?.variation_id.toString() ?? '',
          variation_part_id: variation?.part_id ?? data.product.part_id,
          variation_color: variation?.color ?? '',
          variation_image: variation?.image_large ?? variation?.image ?? '',

          pages: data.pages.map(formatPage),
        },

        event: 'click',
        action: 'product-click',
        ...sharedTrackingData.value,
      };

      return productClickEvent;
    }

    async function sendProductClickEvent(data: BuildProductClick) {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const productClickEvent = buildProductClickEvent(data);
      if (!productClickEvent) return;
      await trackingService.sendProductClickEvent(productClickEvent);
    }

    function buildPageViewEvent(data: BuildPageView) {
      const pageViewEvent: PageViewTracking = {
        source: 'canvas',
        groupings: 'user-tracking-actions.page-view',

        event_data: {
          ...sharedEventData.value,

          pages: data.pages.map(formatPage),
        },

        event: 'read',
        action: 'view_page',
        ...sharedTrackingData.value,
      };

      return pageViewEvent;
    }

    async function sendPageViewEvent(data: BuildPageView) {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const pageViewEvent = buildPageViewEvent(data);
      await trackingService.sendPageViewEvent(pageViewEvent);
    }

    function buildTimeOnPageEvent(data: BuildTimeOnPage) {
      const timeOnPageEvent: TimeOnPageTracking = {
        source: 'canvas',
        groupings: 'user-tracking-actions.time-on-page',

        event_data: {
          ...sharedEventData.value,

          start_view_timestamp: data.start_view_timestamp,
          end_view_timestamp: data.end_view_timestamp,

          pages: data.pages.map(formatPage),
        },

        event: 'read',
        action: 'time_on_page',
        ...sharedTrackingData.value,
      };

      return timeOnPageEvent;
    }

    async function sendTimeOnPageEvent(data: BuildTimeOnPage) {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const timeOnPageEvent = buildTimeOnPageEvent(data);
      await trackingService.sendTimeOnPageEvent(timeOnPageEvent);
    }

    function buildDesignDownloadEvent() {
      const designDownloadEvent: DesignDownloadTracking = {
        source: 'layout_toolbar',
        component: 'download_menu_option',
        groupings: 'user-tracking-actions.design-download',

        event_data: {
          ...sharedEventData.value,
        },

        event: 'click',
        action: 'download_design',
        ...sharedTrackingData.value,
      };

      return designDownloadEvent;
    }

    async function sendDesignDownloadEvent() {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const designDownloadEvent = buildDesignDownloadEvent();
      await trackingService.sendDesignDownloadEvent(designDownloadEvent);
    }

    function buildProductLinkClickEvent(data: BuildProductLinkClick) {
      if (!data.product) {
        Sentry.captureMessage(
          'Product not provided to buildProductLinkClickEvent'
        );
        return;
      }

      const variation = data.product.variations.find(
        (variation) => variation.part_id === data.product?.part_id
      );

      const productLinkClickEvent: ProductLinkClickTracking = {
        source: data.source,
        component: data.component,
        groupings: 'user-tracking-actions.product-url-click',

        event_data: {
          ...sharedEventData.value,

          product_id: data.product.product_id,
          product_name: data.product.name,
          product_category: data.product.category,
          product_brand: data.product.brand,
          product_style_id: data.product.style_id,
          product_style_name: data.product.style_name,
          product_url: data.product.product_url,
          variation_id: variation?.variation_id.toString() ?? '',
          variation_part_id: variation?.part_id ?? data.product.part_id,
          variation_color: variation?.color ?? '',

          pages: data.pages.map(formatPage),
        },

        event: 'click',
        action: 'open_product_url',
        ...sharedTrackingData.value,
      };

      return productLinkClickEvent;
    }

    async function sendProductLinkClickEvent(data: BuildProductLinkClick) {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const productLinkClickEvent = buildProductLinkClickEvent(data);
      if (!productLinkClickEvent) return;
      await trackingService.sendProductLinkClickEvent(productLinkClickEvent);
    }

    function buildProductImageDownloadEvent(data: BuildProductImageDownload) {
      if (!data.product) {
        Sentry.captureMessage(
          'Product not provided to buildProductImageDownloadEvent'
        );
        return;
      }

      const variation = data.product.variations.find(
        (variation) =>
          variation.image_large === data.imageUrl ||
          variation.image === data.imageUrl
      );

      const productImageDownloadEvent: ProductImageDownloadTracking = {
        source: 'sidebar',
        component: 'download_image_button',
        groupings: 'user-tracking-actions.product-image-download',

        event_data: {
          ...sharedEventData.value,

          product_id: data.product.product_id,
          product_name: data.product.name,
          product_category: data.product.category,
          product_brand: data.product.brand,
          product_style_id: data.product.style_id,
          product_style_name: data.product.style_name,
          variation_id: variation?.variation_id.toString() ?? '',
          variation_part_id: variation?.part_id ?? data.product.part_id,
          variation_color: variation?.color ?? '',
          variation_image: variation?.image_large ?? variation?.image ?? '',

          pages: data.pages.map(formatPage),
        },

        event: 'click',
        action: 'download_image',
        ...sharedTrackingData.value,
      };

      return productImageDownloadEvent;
    }

    async function sendProductImageDownloadEvent(
      data: BuildProductImageDownload
    ) {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const productImageDownloadEvent = buildProductImageDownloadEvent(data);
      if (!productImageDownloadEvent) return;
      await trackingService.sendProductImageDownloadEvent(
        productImageDownloadEvent
      );
    }

    const pageViewDebouncer = debounce((currentPages: Page[]) => {
      if (!settings.SEND_TRACKING_EVENTS) return;
      previousCurrentPages.value = currentPages;
      sendPageViewEvent({ pages: currentPages });
    }, 1000);

    const timeOnPageThrottler = throttle(() => {
      if (!settings.SEND_TRACKING_EVENTS) return;
      const hasStartViewTimestamp = computed(
        () => startViewTimestamp.value !== null
      );
      if (hasStartViewTimestamp.value) {
        sendTimeOnPageEvent({
          start_view_timestamp: startViewTimestamp.value ?? 0,
          end_view_timestamp: Date.now(),
          pages: previousCurrentPages.value,
        });
        startViewTimestamp.value = null;
      }
      if (!hasStartViewTimestamp.value) {
        startViewTimestamp.value = Date.now();
      }
    }, 1000);

    useEventListener(document, 'visibilitychange', () => {
      if (!settings.SEND_TRACKING_EVENTS) return;
      if (document.visibilityState === 'hidden') {
        const timeOnPageEvent = buildTimeOnPageEvent({
          start_view_timestamp: startViewTimestamp.value ?? 0,
          end_view_timestamp: Date.now(),
          pages: previousCurrentPages.value,
        });
        startViewTimestamp.value = null;

        trackingService.sendTimeOnPageEvent(timeOnPageEvent, {
          isKeepAliveRequest: true,
        });
      } else {
        startViewTimestamp.value = Date.now();
      }
    });

    return [
      reactive({}),
      {
        sendProductClickEvent,
        sendPageViewEvent,
        sendTimeOnPageEvent,
        sendDesignDownloadEvent,
        sendProductLinkClickEvent,
        sendProductImageDownloadEvent,
        pageViewDebouncer,
        timeOnPageThrottler,
      },
    ] as const;
  });

export { useMakeTrackingInstance, useTrackingInstance, useTrackingHandlers };
